'use client';

import React from 'react';

interface BreathingOrbProps {
  isActive: boolean;
  audioLevel: number;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

const sizeConfig = {
  sm: { orb: 'w-12 h-12', ring1: 'w-16 h-16', ring2: 'w-20 h-20', text: 'text-xs' },
  md: { orb: 'w-16 h-16', ring1: 'w-20 h-20', ring2: 'w-24 h-24', text: 'text-sm' },
  lg: { orb: 'w-24 h-24', ring1: 'w-32 h-32', ring2: 'w-40 h-40', text: 'text-sm' },
};

/**
 * Tutor speaking indicator - breathing orb style
 */
export function BreathingOrb({ isActive, audioLevel, size = 'md', label }: BreathingOrbProps) {
  const scale = isActive ? 1 + (audioLevel / 100) * 0.2 : 1;
  const config = sizeConfig[size];

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative flex items-center justify-center">
        {/* Glow rings */}
        {isActive && (
          <>
            <div
              className={`absolute ${config.ring1} rounded-full bg-[color:var(--primary-200)] opacity-40 animate-ping`}
              style={{ animationDuration: '2s' }}
            />
            <div
              className={`absolute ${config.ring2} rounded-full bg-[color:var(--primary-100)] opacity-30 animate-ping`}
              style={{ animationDuration: '2.5s' }}
            />
          </>
        )}

        {/* Main orb */}
        <div
          className={`relative ${config.orb} rounded-full transition-all duration-150 ${
            isActive
              ? 'bg-[color:var(--primary-500)] shadow-md'
              : 'bg-[color:var(--surface-3)]'
          }`}
          style={{ transform: `scale(${scale})` }}
        >
          <div className="absolute inset-1 rounded-full bg-white/20" />
          {isActive && (
            <div className="absolute inset-2 rounded-full bg-white/20 animate-pulse" />
          )}
        </div>
      </div>

      {label && (
        <span className={`${config.text} font-medium ${isActive ? 'text-[color:var(--primary-600)]' : 'text-[color:var(--gray-500)]'}`}>
          {label}
        </span>
      )}
    </div>
  );
}
